import { isEmpty } from 'lodash';
import { MessagePlugin } from 'tdesign-react';

import { CopyType } from '@/const/lits';
import {
  ActionType,
  ChangeMediaType,
  EventType,
  MissionType,
} from '@/const/mission';
import { IMission } from '@/interface';
import { Status } from '@/interface/aigc';
import { postMaterial } from '@/utils/material';
import { transformObjectDeep } from '@/utils/tans/object';

import { apiPathServer } from './api-config';
import { CgiResp } from './cgi';
import { API_PREFIX, request } from './request';

/**
 * 获取mission数据
 * @param sceneId
 * @param mission
 * @returns
 */
export const getMissionPostData = async (
  sceneId?: string,
  mission?: IMission,
) => {
  const { materialMap, ...postData }: Record<string, any> = mission || {};
  postData.materialMap = postMaterial(postData, materialMap);

  const data = sceneId
    ? { mission: { ...postData }, sceneId }
    : { mission: { ...postData } };
  const result: Record<string, any> = transformObjectDeep(data, 'snake');
  const missionEvent = filterKeyIndex(result?.mission.mission_event) || [];
  const relatedMessages = filterKeyIndex(result?.mission.related_messages) || [];
  const multiBranch = filterKeyIndex(result?.mission.completion_tip?.multi_branch) || [];
  const tempMissionEvents = resetRichMediaValue(missionEvent || []);

  if (result.mission.mission_type === MissionType.MISSION_TYPE_PROPS) {
    const postData = {
      ...result,
      mission: {
        ...result?.mission,
        assets_event: tempMissionEvents,
        bg_image_blur: result.mission?.image_blur,
        bg_image: result.mission?.image,
      },
    };
    delete postData?.mission.custom_card_info;
    delete postData.mission?.mission_event;
    delete postData.mission?.image_blur;
    delete postData.mission?.image;
    delete postData.mission?.image_binary;
    return postData.mission;
  }

  const resultPostData = {
    ...result,
    mission: {
      ...result?.mission,
      mission_event: tempMissionEvents,
      related_messages: relatedMessages,
      completion_tip: {
        ...result?.mission?.completion_tip,
        multiBranch,
      },
    },
  };
  delete resultPostData?.mission?.custom_card_info;
  return resultPostData;
};

/**
 * 过滤掉前端所需的keyIndex
 * @param result
 * @returns
 */
const filterKeyIndex = (result: Record<string, any>[]) => result
  ?.map((e) => {
    if (e.type !== EventType.EVENT_TYPE_EXCL) {
      const temp = e;
      delete temp?.key_index;
      return temp;
    }
    return undefined;
  })
  ?.filter(e => e !== undefined);

/**
 * 保存信息
 */
export const modifyMissionDetail = async (
  sceneId: string,
  mission?: IMission,
  autoSave?: boolean,
  extendsInfo?: {
    force?: boolean,
    close?: boolean,
    pageUniqueKey?: string
  },
) => {
  const postData = await getMissionPostData(sceneId, mission);
  postData.auto_save = autoSave;
  if (mission?.missionType === MissionType.MISSION_TYPE_PROPS) {
    const res = await request(`${API_PREFIX}${apiPathServer.postProps}`, {
      method: 'POST',
      body: postData,
    });
    return transformObjectDeep(res, 'camel') as CgiResp<{ version: string; id: string }>;;
  }
  const res = await request(`${API_PREFIX}${apiPathServer.postMission}`, {
    method: 'POST',
    body:
      transformObjectDeep({
        ...postData,
        ...extendsInfo,
      }, 'snake'),
  });
  return transformObjectDeep(res, 'camel') as CgiResp<{ version: string; id: string }>;;
};

/**
 * 修改媒体数据
 * @param item
 * @returns
 */
export const getMediaValue = (item: any) => {
  const resetValue = {
    asyncStatus: undefined,
    async_status: undefined,
  };

  const type = item?.action.change_media.type;
  if (type === ChangeMediaType.CHANGE_MEDIA_TYPE_BACKGROUND_IMAGE) {
    return { background_image: { ...getValue(item?.action?.change_media?.background_image), ...resetValue } };
  }
  if (type === ChangeMediaType.CHANGE_MEDIA_TYPE_FOREGROUND_IMAGE) {
    return { foreground_image: { ...getValue(item?.action?.change_media?.foreground_image), ...resetValue } };
  }
  if (type === ChangeMediaType.CHANGE_MEDIA_TYPE_BGM_AUDIO
    || type === ChangeMediaType.CHANGE_MEDIA_TYPE_SOUND_EFFECT) {
    return { bgm_audio: { ...getValue(item?.action?.change_media?.bgm_audio), ...resetValue } };
  }
  if (type === ChangeMediaType.CHANGE_MEDIA_TYPE_FULL_VIDEO) {
    return { full_video: { ...getValue(item?.action?.change_media?.full_video), ...resetValue } };
  }
  if (type === ChangeMediaType.CHANGE_MEDIA_TYPE_BACKGROUND_VIDEO) {
    return { background_video: getValue(item?.action?.change_media?.background_video), ...resetValue };
  }
  if (type === ChangeMediaType.CHANGE_MEDIA_TYPE_SPINE_FORE_IMAGE) {
    return { spine: { ...item?.action?.change_media?.spine, ...resetValue } };
  }
  if (type === ChangeMediaType.CHANGE_MEDIA_TYPE_MULTI_CHARACTER) {
    return { multi_vertical_painting: item?.action?.change_media?.multi_vertical_painting };
  }
  return {};
};

/**
 * 获取媒体数据的值
 * @param data
 * @returns
 */
const getValue = (data?: {
  url: any;
  prompt_text: string;
  task_id?: string;
}) => {
  const empty = (isEmpty(data) || isEmpty(data?.url) || isEmpty(data?.url?.url))
    && isEmpty(data?.task_id);
  return empty
    ? { ...data, url: { url: '' }, prompt_text: data?.prompt_text }
    : data;
};

/**
 * 重置媒体数据
 * @param tempMissionEvents
 * @returns
 */
export const resetRichMediaValue = (tempMissionEvents: any[]) => tempMissionEvents.map((item) => {
  if (
    item?.type === EventType.EVENT_TYPE_ACTION
    && item.action.type === ActionType.ACTION_TYPE_CHANGEMEDIA
  ) {
    const changeType: ChangeMediaType = item?.action?.change_media?.type;
    const newItem = {
      ...item,
      action: {
        ...item?.action,
        change_media: {
          operation: item?.action?.change_media?.operation,
          type:
            changeType === ChangeMediaType.CHANGE_MEDIA_TYPE_SOUND_EFFECT
              ? ChangeMediaType.CHANGE_MEDIA_TYPE_BGM_AUDIO
              : changeType,
          ...getMediaValue(item),
        },
      },
    };
    return newItem;
  }
  return item;
});

/**
 * 删除信息
 */
export const deleteMission = async (id: string) => {
  const res = await request(`${API_PREFIX}${apiPathServer.deleteMission}`, {
    method: 'POST',
    body: { id },
  });
  return res;
};

/**
 * 拉取详情
 */
export const fetchMissionDetail = async ({ id = '', release = false, close = false }) => {
  const res = await request<CgiResp<any>>(
    `${API_PREFIX}${apiPathServer.missionDetail}${release ? '/release' : ''}`,
    {
      query: {
        id,
        depth: 2,
        close,
      },
    },
  );
  return transformObjectDeep(res, 'camel') as CgiResp;
};

/**
 * 复制
 * @param id
 * @returns
 */
export const copyMission = async (id: string) => {
  const res = await request(`${API_PREFIX}${apiPathServer.copy}`, {
    method: 'POST',
    body: { copy_id: id, type: CopyType.MISSION },
  });
  return res;
};

/**
 * 智能创作
 * @param id
 * @returns
 */
export const generateMission = async (sceneId: string, desc: string) => {
  const res = await request<CgiResp<string>>(
    `${API_PREFIX}${apiPathServer.generateMission}`,
    {
      method: 'POST',
      body: transformObjectDeep({ sceneId, plotDescription: desc }, 'snake'),
    },
  );
  if (res.code !== 0) {
    MessagePlugin.error(res.message
      || res.msg
      || `${apiPathServer.generateMission} failed: 后台服务错误`);
    return;
  }
  return res.data || '';
};

/**
 * 音视频异步任务
 */
export const createTranscodeTask = async (
  cosUrl: string,
  type: 'nova_music' | 'nova_video',
) => {
  const res = await request<CgiResp<{ task_id: string }>>(
    `${API_PREFIX}${apiPathServer.asyncTranscode}`,
    {
      method: 'POST',
      body: transformObjectDeep({ url: cosUrl, mediaType: type }, 'snake'),
    },
  );
  if (res.code !== 0 || isEmpty(res.data.task_id)) {
    MessagePlugin.error(res.message
      || res.msg
      || `${apiPathServer.asyncTranscode} failed: 后台服务错误`);
    return;
  }
  return res.data.task_id || '';
};

/**
 * 图片取色异步任务
 */
export const createExtractColorTask = async (cosUrl: string) => {
  const res = await request<CgiResp<{ task_id: string }>>(
    `${API_PREFIX}${apiPathServer.asyncExtractColor}`,
    {
      method: 'POST',
      body: transformObjectDeep({ imageUrl: cosUrl }, 'snake'),
    },
  );
  if (res.code !== 0 || isEmpty(res.data.task_id)) {
    MessagePlugin.error(res.message
      || res.msg
      || `${apiPathServer.asyncExtractColor} failed: 后台服务错误`);
    return;
  }
  return res.data.task_id || '';
};

export interface IAsyncTaskProcess {
  taskId: string;
  url?: string;
  status: Status;
  extractColor?: {
    primary: string;
    complementary: string;
  };
}

export interface IGetAsyncTaskProcessRes {
  processedMedias: IAsyncTaskProcess[];
}

/**
 * 获取异步任务结果
 */
export const getAsyncTaskProcess = async (
  taskIds: string[],
  signal?: AbortSignal,
) => {
  const res = await request<CgiResp<IGetAsyncTaskProcessRes>>(
    `${API_PREFIX}${apiPathServer.getAsyncTaskProcess}`,
    {
      method: 'POST',
      body: transformObjectDeep({ taskIds }, 'snake'),
      signal,
    },
  );
  if (res.code !== 0) {
    if (res.code === 40001) {
      throw new Error('cancel');
    }
    MessagePlugin.error(res.message
      || res.msg
      || `${apiPathServer.getAsyncTaskProcess} failed: 后台服务错误`);
    return [];
  }
  return (
    transformObjectDeep<IGetAsyncTaskProcessRes>(res.data, 'camel')
      ?.processedMedias || []
  );
};
